import { useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  REGEN_SCOPES,
  useGrantCredits,
  useRefundPayment,
  useRegenerateJob,
  useSetAdmin,
  useStartRegenerate,
  useUserCredits,
  useUserPayments,
  type PaymentRow,
} from "@/api/admin/users";
import { ApiError } from "@/api/client";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { PLATFORM_KEY, displayName, identLabel } from "./userIdentity";

/**
 * DUE-100 Phase F — User detail page. Re-keyed onto `users.id` in DUE-191:
 * iOS guest / Apple-only accounts have no `tg_id`, so the page (and its
 * route, `/admin/users/:userId`) key off the numeric `users.id` instead.
 *
 * Reads `/api/admin/users/{user_id}/credits` and offers admin actions:
 *   - Grant credits  → POST /api/admin/credits/grant (`target_user_id`)
 *   - Refund payment → POST /api/admin/refund
 *   - Admin access   → PATCH /api/admin/users/{user_id}/admin (DUE-180)
 *
 * Block / Reset user is a separate ticket (DUE-230) — no stub lives here
 * until the backend endpoints exist.
 *
 * Status copy uses i18n keys (no glyphs) — mirrors how Phase E surfaced
 * `free_tier.updated` / `free_tier.no_changes`.
 *
 * AdminBlock note (DUE-180): `UserCreditsView` (the payload this page
 * already fetches) carries the target's live `is_admin` flag, so the
 * toggle initializes from server-confirmed state rather than assuming
 * `false`. After a successful PATCH the local state is updated from the
 * response directly (the `["admin","users"]` invalidation from
 * `useSetAdmin` also refetches this credits query in the background).
 */

const PAGE: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const CARD: CSSProperties = {
  background: "var(--d-ink-2)",
  borderRadius: 22,
  padding: 16,
  border: "1px solid var(--d-line)",
};
const ROW: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  padding: "4px 0",
  fontFamily: "var(--font-body)",
  fontSize: 14,
};
const NUM: CSSProperties = {
  fontFamily: "var(--font-mono)",
  color: "var(--d-fg)",
};
const BTN: CSSProperties = {
  padding: "8px 14px",
  borderRadius: 999,
  border: "1px solid var(--d-violet)",
  background: "transparent",
  color: "var(--d-violet)",
  cursor: "pointer",
  fontFamily: "var(--font-body)",
};
const BTN_DISABLED: CSSProperties = {
  ...BTN,
  opacity: 0.4,
  cursor: "not-allowed",
};
const INPUT: CSSProperties = {
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-mono)",
};
const STATUS: CSSProperties = { fontSize: 12, color: "var(--d-fg-muted)" };
const TABLE: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  marginTop: 8,
};
const TH: CSSProperties = {
  textAlign: "left",
  padding: "4px 6px",
  borderBottom: "1px solid var(--d-line)",
  color: "var(--d-fg-muted)",
  fontFamily: "var(--font-body)",
};
const TD: CSSProperties = {
  padding: "4px 6px",
  borderBottom: "1px solid var(--d-line)",
};

const FEATURES = [
  "synastry",
  "composite",
  "transits_days",
  "legacy_credit",
  "relation_named",
  "relation_words",
] as const;

export function UserDetailPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const { userId: userIdStr } = useParams();
  const userId = Number(userIdStr);
  const { data, isLoading, isError, refetch } = useUserCredits(
    Number.isFinite(userId) ? userId : null,
  );
  const grant = useGrantCredits();
  const refund = useRefundPayment();
  const setAdmin = useSetAdmin();
  const [status, setStatus] = useState<string | null>(null);

  if (isError) return <AdminErrorState onRetry={() => void refetch()} />;

  if (isLoading || !data) {
    return <div style={PAGE}>{t("common.loading")}</div>;
  }

  // Fallback for the refund-confirm copy: prefer the same label the heading
  // shows, else the identifier column (always non-null).
  const userLabel = displayName(data) ?? identLabel(data);

  return (
    <div style={PAGE}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22 }}>
        {displayName(data) ?? t("users.no_name")}
      </h2>
      <div style={{ ...STATUS, fontFamily: "var(--font-body)" }}>
        <span>{t(PLATFORM_KEY[data.platform])}</span> ·{" "}
        <span>{identLabel(data)}</span>
      </div>

      <div style={CARD}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
          {t("users.detail.balance")}
        </div>
        <div style={ROW}>
          <span>{t("users.detail.synastry")}</span>
          <span style={NUM}>{data.synastry_credits}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.composite")}</span>
          <span style={NUM}>{data.composite_credits}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.relation_named")}</span>
          <span style={NUM}>{data.relation_named_credits}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.relation_words")}</span>
          <span style={NUM}>{data.relation_words_credits}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.transits_until")}</span>
          <span style={NUM}>{data.transits_until ?? "—"}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.legacy")}</span>
          <span style={NUM}>{data.legacy_credits}</span>
        </div>
        <div style={ROW}>
          <span>{t("users.detail.total_llm")}</span>
          <span style={NUM}>${data.total_llm_cost_usd.toFixed(2)}</span>
        </div>
      </div>

      <GrantBlock
        userId={data.id}
        onGrant={(args) =>
          grant.mutate(args, {
            onSuccess: () => setStatus(t("users.detail.status.granted")),
            onError: (e) => setStatus((e as Error).message),
          })
        }
      />

      <PaymentsBlock
        userId={data.id}
        userLabel={userLabel}
        refund={refund}
        setStatus={setStatus}
      />

      <AdminBlock
        userId={data.id}
        isAdmin={data.is_admin ?? false}
        setAdmin={setAdmin}
        setStatus={setStatus}
      />

      <RegenerateBlock userId={data.id} setStatus={setStatus} />

      {status && (
        <div role="status" style={STATUS}>
          {status}
        </div>
      )}

      <div style={CARD}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
          {t("users.detail.history")}
        </div>
        <table style={TABLE}>
          <thead>
            <tr>
              <th style={TH}>when</th>
              <th style={TH}>feature</th>
              <th style={{ ...TH, textAlign: "right" }}>amount</th>
              <th style={TH}>reason</th>
            </tr>
          </thead>
          <tbody>
            {data.history.map((row) => (
              <tr key={row.id}>
                <td style={TD}>
                  {new Date(row.created_at)
                    .toISOString()
                    .slice(0, 19)
                    .replace("T", " ")}
                </td>
                <td style={TD}>{row.feature}</td>
                <td style={{ ...TD, textAlign: "right" }}>{row.amount}</td>
                <td style={TD}>{row.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GrantBlock(props: {
  userId: number;
  onGrant: (args: {
    target_user_id: number;
    amount: number;
    feature: string;
  }) => void;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const [amount, setAmount] = useState("1");
  const [feature, setFeature] = useState<string>(FEATURES[0]);
  const numericAmount = Number(amount);
  const disabled = !Number.isFinite(numericAmount) || numericAmount <= 0;
  return (
    <div style={CARD}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
        {t("users.detail.action.grant")}
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginTop: 8,
          flexWrap: "wrap",
        }}
      >
        <select
          aria-label="feature"
          style={INPUT}
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
        >
          {FEATURES.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        <input
          aria-label="amount"
          style={INPUT}
          type="number"
          min={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <button
          style={disabled ? BTN_DISABLED : BTN}
          disabled={disabled}
          onClick={() =>
            props.onGrant({
              target_user_id: props.userId,
              amount: numericAmount,
              feature,
            })
          }
        >
          {t("users.detail.action.grant")}
        </button>
      </div>
    </div>
  );
}

const MODAL_BACKDROP: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
  padding: 24,
};
const MODAL_CARD: CSSProperties = {
  ...CARD,
  maxWidth: 360,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const BTN_PRIMARY: CSSProperties = {
  height: 48,
  borderRadius: 999,
  border: "none",
  background: "var(--d-violet)",
  color: "var(--d-ink)",
  cursor: "pointer",
  fontFamily: "var(--font-body)",
  padding: "0 20px",
};
const BTN_GHOST: CSSProperties = {
  height: 48,
  borderRadius: 999,
  border: "1px solid var(--d-line)",
  background: "transparent",
  color: "var(--d-fg)",
  cursor: "pointer",
  fontFamily: "var(--font-body)",
  padding: "0 20px",
};

function PaymentsBlock(props: {
  userId: number;
  userLabel: string;
  refund: ReturnType<typeof useRefundPayment>;
  setStatus: (s: string) => void;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const { data, isLoading } = useUserPayments(props.userId);
  const [pending, setPending] = useState<PaymentRow | null>(null);

  const onConfirm = () => {
    if (!pending) return;
    const target = pending;
    setPending(null);
    props.refund.mutate(
      { payment_id: target.id },
      {
        onSuccess: () => props.setStatus(t("users.detail.status.refunded")),
        onError: (e) =>
          props.setStatus(
            (e as Error).message || t("users.detail.status.refund_error"),
          ),
      },
    );
  };

  return (
    <div style={CARD}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
        {t("users.detail.payments")}
      </div>
      {isLoading ? (
        <div style={STATUS}>{t("common.loading")}</div>
      ) : !data || data.length === 0 ? (
        <div style={STATUS}>{t("users.detail.no_payments")}</div>
      ) : (
        <table style={TABLE}>
          <thead>
            <tr>
              <th style={TH}>{t("users.detail.col_date")}</th>
              <th style={TH}>{t("users.detail.col_sku")}</th>
              <th style={{ ...TH, textAlign: "right" }}>
                {t("users.detail.col_amount")}
              </th>
              <th style={TH}>{t("users.detail.col_status")}</th>
              <th style={{ ...TH, textAlign: "right" }}>
                {t("users.detail.col_action")}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((p) => (
              <tr key={p.id}>
                <td style={TD}>{new Date(p.created_at).toISOString().slice(0, 10)}</td>
                <td style={TD}>{p.product_code}</td>
                <td style={{ ...TD, textAlign: "right" }}>{p.amount_xtr ?? "—"}</td>
                <td style={TD}>
                  {t(`users.detail.pstatus.${p.status}`, {
                    defaultValue: p.status,
                  })}
                </td>
                <td style={{ ...TD, textAlign: "right" }}>
                  {p.refundable ? (
                    <button
                      style={BTN}
                      disabled={props.refund.isPending}
                      onClick={() => setPending(p)}
                    >
                      {t("users.detail.action.refund_stars")}
                    </button>
                  ) : (
                    <span style={STATUS}>
                      {t(`users.detail.pstatus.${p.status}`, {
                        defaultValue: p.status,
                      })}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pending && (
        <div
          style={MODAL_BACKDROP}
          role="dialog"
          aria-modal="true"
          aria-label={t("users.detail.confirm.title")}
        >
          <div style={MODAL_CARD}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 18 }}>
              {t("users.detail.confirm.title")}
            </div>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 14 }}>
              {t("users.detail.confirm.body", {
                amount: pending.amount_xtr ?? "?",
                user: props.userLabel,
                sku: pending.product_code,
              })}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button style={BTN_GHOST} onClick={() => setPending(null)}>
                {t("users.detail.confirm.cancel")}
              </button>
              <button style={BTN_PRIMARY} onClick={onConfirm}>
                {t("users.detail.confirm.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** True for the 400 the backend raises when an admin tries to change their
 * own `is_admin` flag (`{"detail": {"code": "self_admin_change", ...}}` —
 * see `set_admin` in `src/api/routers/admin.py`). */
function isSelfAdminChangeError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 400) return false;
  const body = err.body;
  if (typeof body !== "object" || body === null || !("detail" in body)) {
    return false;
  }
  const detail = (body as { detail?: unknown }).detail;
  return (
    typeof detail === "object" &&
    detail !== null &&
    (detail as { code?: unknown }).code === "self_admin_change"
  );
}

function AdminBlock(props: {
  userId: number;
  isAdmin: boolean;
  setAdmin: ReturnType<typeof useSetAdmin>;
  setStatus: (s: string) => void;
}): JSX.Element {
  const { t } = useTranslation("admin");
  // Initialized from the server-confirmed flag (DUE-180 Finding 1); kept in
  // local state afterwards so a successful PATCH updates the label
  // immediately without waiting for the credits query to refetch.
  const [isAdmin, setIsAdmin] = useState(props.isAdmin);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onConfirm = () => {
    const nextIsAdmin = !isAdmin;
    setConfirmOpen(false);
    props.setAdmin.mutate(
      { userId: props.userId, is_admin: nextIsAdmin },
      {
        onSuccess: (data) => {
          setIsAdmin(data.is_admin);
          props.setStatus(t("users.detail.admin.status.updated"));
        },
        onError: (e) =>
          props.setStatus(
            isSelfAdminChangeError(e)
              ? t("users.detail.admin.self_error")
              : (e as Error).message,
          ),
      },
    );
  };

  return (
    <div style={CARD}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
        {t("users.detail.admin.title")}
      </div>
      <div style={{ marginTop: 8 }}>
        <button
          style={BTN}
          disabled={props.setAdmin.isPending}
          onClick={() => setConfirmOpen(true)}
        >
          {isAdmin
            ? t("users.detail.admin.revoke")
            : t("users.detail.admin.grant")}
        </button>
      </div>
      <ConfirmModal
        open={confirmOpen}
        title={t("users.detail.admin.title")}
        body={
          isAdmin
            ? t("users.detail.admin.confirm_revoke")
            : t("users.detail.admin.confirm_grant")
        }
        destructive={isAdmin}
        onConfirm={onConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}

/** True for the 409 the backend raises while a regeneration job is already
 * running for this user (`{"detail": {"code": "regenerate_running", ...}}`
 * — see `post_regenerate` in `src/api/routers/admin.py`, DUE-296). */
function isRegenerateRunningError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const body = err.body;
  if (typeof body !== "object" || body === null || !("detail" in body)) {
    return false;
  }
  const detail = (body as { detail?: unknown }).detail;
  return (
    typeof detail === "object" &&
    detail !== null &&
    (detail as { code?: unknown }).code === "regenerate_running"
  );
}

/** DUE-296 — delete-then-regenerate the user's existing readings with the
 * current prompts/config. Four scope checkboxes → ConfirmModal → POST;
 * the job snapshot polls every 2 s while running (`useRegenerateJob`). */
function RegenerateBlock(props: {
  userId: number;
  setStatus: (s: string) => void;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const { data: job } = useRegenerateJob(props.userId);
  const start = useStartRegenerate();
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(REGEN_SCOPES.map((s) => [s, true])),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const scopes = REGEN_SCOPES.filter((s) => checked[s]);
  const disabled =
    scopes.length === 0 || start.isPending || job?.status === "running";

  const onConfirm = () => {
    setConfirmOpen(false);
    start.mutate(
      { userId: props.userId, scopes },
      {
        onError: (e) =>
          props.setStatus(
            isRegenerateRunningError(e)
              ? t("users.detail.regenerate.already_running")
              : (e as Error).message,
          ),
      },
    );
  };

  const done = job?.items.filter((i) => i.state === "done").length ?? 0;
  const failed = job?.items.filter((i) => i.state === "failed").length ?? 0;

  return (
    <div style={CARD}>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 16 }}>
        {t("users.detail.regenerate.title")}
      </div>
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginTop: 8,
          flexWrap: "wrap",
          fontFamily: "var(--font-body)",
          fontSize: 14,
        }}
      >
        {REGEN_SCOPES.map((s) => (
          <label
            key={s}
            style={{ display: "flex", gap: 6, alignItems: "center" }}
          >
            <input
              type="checkbox"
              checked={checked[s] ?? false}
              onChange={(e) =>
                setChecked((c) => ({ ...c, [s]: e.target.checked }))
              }
            />
            {t(`users.detail.regenerate.scope.${s}`)}
          </label>
        ))}
      </div>
      <div style={{ marginTop: 8 }}>
        <button
          style={disabled ? BTN_DISABLED : BTN}
          disabled={disabled}
          onClick={() => setConfirmOpen(true)}
        >
          {t("users.detail.regenerate.button")}
        </button>
      </div>
      {job && job.status !== "idle" && (
        <div
          style={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {job.items.map((item, i) => (
            <div key={i} style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
              {item.scope} · {item.partner_name ?? item.day ?? ""} · {item.state}
              {item.state === "failed" && item.error ? ` — ${item.error}` : ""}
            </div>
          ))}
          {job.status === "done" && (
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                marginTop: 4,
              }}
            >
              {t("users.detail.regenerate.summary", { done, failed })}
            </div>
          )}
        </div>
      )}
      <ConfirmModal
        open={confirmOpen}
        title={t("users.detail.regenerate.confirm_title")}
        body={t("users.detail.regenerate.confirm_body")}
        destructive
        onConfirm={onConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
